/**
 * RECORD-1 — who reaches the capture route.
 *
 * THE REQUIREMENT (COMMAND 7 Oct 18:35 (2)): the owner must be able to run a 45-minute test on his
 * own handset IN PRODUCTION while every teacher at the pilot school still sees the feature off. The
 * global `REACT_APP_RECORD_V1` cannot do both, because switching it on gives in-app recording to
 * every teacher served that build at once.
 *
 * MY FIRST VERSION OF THIS WAS WRONG AND COMMAND WAS RIGHT TO REFUSE IT (19:46). It read a
 * configured list of emails and user ids out of the build/runtime config and matched the signed-in
 * user against it. **THE BUNDLE IS PUBLIC.** Anything named in it — an address, a user id, the shape
 * of who gets early access — ships to every browser that loads the app and can be read by anyone.
 * A list of people does not belong in a frontend artefact, however the deployment sets it, and
 * "it is only configuration" is not a defence when the artefact is downloadable.
 *
 * SO THE DECISION IS THE SERVER'S AND THIS ONLY READS IT. `record_live_enabled` is a per-user
 * boolean the server puts on the signed-in user; the client asks no question about identity, holds
 * no list, and matches nothing. Nothing here can leak who is on it, because nothing here knows.
 *
 * MEASURED BEFORE BUILDING THIS: no per-user or per-org feature setting reaches the client today.
 * The frontend reads exactly `role`, `tenant` and `tenant_role` off the user. So the field below is
 * owed by Track A per COMMAND 19:46, and until it ships the expression is simply false and the
 * global flag is the only way in — which is today's behaviour, unchanged.
 *
 * STILL A ROLLOUT GATE, NEVER AN AUTHORISATION BOUNDARY. It decides which build renders a route.
 * The boundary is the upload's own authorisation, which is unchanged and server-side.
 */

/**
 * The per-user boolean the server provides. Named as a constant so the contract with Track A is one
 * string in one place rather than a field name spelled out at each site that reads it.
 */
export const SERVER_GATE_FIELD = "record_live_enabled";

/**
 * Does this user get the capture route?
 *
 * RECORD-FLAG-ONE-1 — THE CLIENT BUILD FLAG IS GONE and this takes one argument now. It used to
 * accept `{ enabled }`, the global `REACT_APP_RECORD_V1`, as an OR beside the server's field: two
 * switches that could disagree, and the global one granted recording to every teacher served that
 * build at once — which is precisely what the per-user field was introduced to avoid. The server's
 * decision is the only one left.
 *
 * @param {object|null} user  the signed-in user, or null before auth resolves
 */
export function recordV1EnabledFor(user) {
  // STRICTLY `true`, never truthy. A server that sent the string "false", or the number 0, or an
  // empty object, would otherwise switch the feature ON for that user — and a rollout gate that
  // opens on a malformed value is not a gate. Absent means off, which is the default.
  return user?.[SERVER_GATE_FIELD] === true;
}

/**
 * RECORD-ENTRY-1 — THE ROUTE STRING, ONCE.
 *
 * The button and the route must point at the same path or the button is a 404 with a friendly label
 * on it. A literal written in two files agrees by luck; this agrees by construction.
 */
export const RECORD_LIVE_ROUTE = "/record/live";

/**
 * Does this teacher see a door to the recorder — which is the same question as whether the route
 * exists for her, asked once so the two answers cannot differ.
 *
 * THIS LIVED IN ITS OWN FILE UNTIL FIX 3, and the reason it no longer needs to is worth keeping.
 * `recordEntry.js` existed because this module's guard (recordAccess.test.js) forbids
 * `runtimeConfig`, `process.env` and `getConfigValue` here — a gate that can read configuration is
 * a gate that can read a LIST, and the bundle is public. The helper needed the build flag, so it
 * could not live here. With the flag gone it reads nothing, the split has no content, and a
 * pass-through whose docblock explains a constraint that no longer applies is just a stale comment
 * one indirection up. The guard is untouched and still forbids config access.
 */
export function recordLiveVisibleFor(user) {
  return recordV1EnabledFor(user);
}

export default recordV1EnabledFor;
